import { useMutation } from "@apollo/client/react";
import {
  ContentListFragment,
  FolderAdminPageData,
  FolderAdminPageDataVariables,
  FolderAdminPageData_folderById,
  FolderAdminPageData_folderById_elements,
  FolderType,
  ReorderFolderElementInput,
  ReorderFolderElementVariables,
} from "__generated__/types";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import {
  contentStatusFilterValueLabel,
  contentStatusFilterValueToSelectOption,
} from "common/content-status-filter";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import {
  ListElement,
  ListElementFolderElement,
} from "domain/admin/common/AdministrationList";
import { CreateContent, Parent } from "domain/admin/common/CreateContent";
import { CreateContentFooterBox } from "domain/admin/common/CreateContentFooterBox";
import { getListElementType } from "domain/admin/common/utils";
import { adminFolderRootFragment } from "domain/admin/fragments";
import { isInSyllabus } from "domain/common/utils";
import { ContentStatusFilterValue } from "domain/constants";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import React from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { chainFilters, listFilter, noDeletedStatusFilter } from "../util";

// Dynamic import to cause code split for react-dnd library.
const AdministrationList = React.lazy(() =>
  import("domain/admin/common/AdministrationList").then((it) => ({
    default: it.AdministrationList,
  })),
);

const GET_FOLDER = gql`
  query FolderAdminPageData($folderId: ID!) {
    folderById(id: $folderId) {
      ...ContentBreadcrumbFragment
      ...AdminFolderRootFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminFolderRootFragment}
`;

type RouteProps = {
  folderId: string;
};

const MUTATION = gql`
  mutation ReorderFolderElement($input: ReorderFolderElementInput!) {
    reorderFolderElement(input: $input) {
      folder {
        id
      }
    }
  }
`;

const convertToList = (
  data: FolderAdminPageData_folderById_elements[],
  folder: FolderAdminPageData_folderById,
): ListElement[] => {
  return data.map((it) => ({
    category: "FolderElement",
    id: it.id,
    type: getListElementType(it.content),
    status: it.content.status,
    folderElementId: it.id,
    folderId: folder.id,
    title: it.content.title,
    contentId: it.content.id,
  }));
};

export const FolderAdminPage: React.FC = () => {
  const { folderId } = useParams<RouteProps>();

  const [contentStatusFilterValue, setContentStatusFilterValue] =
    React.useState<ContentStatusFilterValue>(ContentStatusFilterValue.ALL);

  if (!folderId) {
    throw new Error("Unexpected error: Missing folderId");
  }

  const { data, loadingOrError, refetch } = useSimpleQuery<
    FolderAdminPageData,
    FolderAdminPageDataVariables
  >(GET_FOLDER, {
    variables: {
      folderId,
    },
  });

  const [mutate, { error }] = useMutation<
    ReorderFolderElementInput,
    ReorderFolderElementVariables
  >(MUTATION);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.folderById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const handleSaveListOrder = async (
    id: string,
    moveToAfterElement: ListElementFolderElement | null,
  ) => {
    await mutate({
      variables: {
        input: {
          folderId,
          elementId: id,
          moveToAfterElementId: moveToAfterElement?.id,
        },
      },
    });

    return refetch();
  };

  const filteredElementsList = data.folderById.elements.filter(
    chainFilters(
      (e) => noDeletedStatusFilter(e.content.status),
      (e) =>
        listFilter(e.content as ContentListFragment, contentStatusFilterValue),
    ),
  );

  const { title, context, type, elements, id } = data.folderById;

  const getParentType = (folderType: FolderType): Parent => {
    switch (folderType) {
      case FolderType.GENERIC:
        return "folder";
      case FolderType.SELF_ASSESSMENT_EXERCISES:
        return "selfAssessment";
      case FolderType.SELF_ASSESSMENT_EXERCISES_CONTAINER:
        return "selfAssessmentsContainer";
    }
  };

  return (
    <AdministrationLayout title={title} context={context ?? []}>
      <Link to={urls.folderAdminEdit(id)}>
        Rediger {type === FolderType.GENERIC ? "mappe" : "selvtest"}detaljer
      </Link>
      <br />
      <Link to={urls.folder(id)}>Studentvisning</Link>
      <div>
        <Select
          name="status"
          title="Status"
          options={Object.values(ContentStatusFilterValue).map(
            contentStatusFilterValueToSelectOption,
          )}
          value={contentStatusFilterValue.toString()}
          onChange={(newValue: string) => {
            setContentStatusFilterValue(newValue as ContentStatusFilterValue);
          }}
        />
      </div>
      {elements.length === 0 ? (
        <p>
          {type === FolderType.GENERIC ? "Mappen" : "Selvtesten"} har ikke noe
          innhold enda.
        </p>
      ) : (
        filteredElementsList.length === 0 && (
          <p>
            Mappen har ikke noe innhold med status &quot;
            {contentStatusFilterValueLabel(contentStatusFilterValue)}&quot;.
          </p>
        )
      )}
      {filteredElementsList.length > 0 && (
        <AdministrationList
          showElementIcons
          isInSyllabus={isInSyllabus(context)}
          elements={convertToList(filteredElementsList, data.folderById)}
          onSaveListOrder={handleSaveListOrder}
          canDelete
          complete={refetch}
        />
      )}
      <MutationLoadingOrError
        // TODO: Do we want to show loading indication when changing order?
        loading={false}
        error={error}
      />
      <CreateContentFooterBox
        buttonText="Legg til innhold"
        content={(props) => (
          <CreateContent
            {...props}
            parent={getParentType(type)} // TODO: Create object to hold both these names and norwegian names?
            parentId={folderId}
          />
        )}
        onUpdated={() => {
          void refetch();
        }}
      />
    </AdministrationLayout>
  );
};
